import { Component, OnInit, input, signal, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SharedModule } from '../../../shared/shared.module';
import { SelectButtonModule } from 'primeng/selectbutton';
import { forkJoin } from 'rxjs';
import { StaffApiService } from '../../../core/api/services';
import { OfflineDataService } from '../../../core/services/offline-data.service';
import { Router } from '@angular/router';
import { BehaviorBipInfoModel } from '../../../core/api/models/behavior-bip-info-model';
import { BehaviorFunctionModel } from '../../../core/api/models/behavior-function-model';
import { BipFunctionDisplayItem, mapBehaviorBipFunctionsToDisplay } from '../bip-function-display.util';
import { PatientBehaviorModel } from '../../../core/api/models/patient-behavior-model';
import { PatientModel } from '../../../core/api/models/patient-model';
import { formatDate } from '@angular/common';
import { AuthService } from '../../../auth/auth.service';

@Component({
    selector: 'app-bip-quick-reference',
    standalone: true,
    imports: [CommonModule, FormsModule, SharedModule, SelectButtonModule],
    templateUrl: './bip-quick-reference.component.html',
    styleUrl: '../../supervisor-main.component.css'
})
export class BipQuickReferenceComponent implements OnInit {
    patientID = input.required<string>();
    loading = signal<boolean>(false);
    isCurrentView: boolean = true; // false = Draft, true = Current
    showToggle = input<boolean>(true);
    isInStaffContext = input<boolean>(false);
    canEditBip = false;
    @Output() strategySelected = new EventEmitter<{ fragment: string }>();

    // Normalize optional input to a boolean defaulting to true
    shouldShowToggle(): boolean {
        const value = this.showToggle();
        return (value ?? true) && this.canEditBip;
    }

    // Lookups
    private functions = signal<BehaviorFunctionModel[]>([]);
    private behaviorsLookup = signal<Map<number, string>>(new Map<number, string>());
    private behaviorsList = signal<PatientBehaviorModel[]>([]);

    // Raw lists
    private draftInfos = signal<BehaviorBipInfoModel[]>([]);
    private currentInfos = signal<BehaviorBipInfoModel[]>([]);

    // Display
    private behaviorsDraft = signal<QuickRefBehaviorRow[]>([]);
    private behaviorsCurrent = signal<QuickRefBehaviorRow[]>([]);
    behaviors = signal<QuickRefBehaviorRow[]>([]);

    // Patient header
    private patient = signal<PatientModel | null>(null);

    constructor(
        private staffApiService: StaffApiService,
        private router: Router,
        private offlineDataService: OfflineDataService,
        private authService: AuthService
    ) {
    }

    ngOnInit(): void {
        this.canEditBip = this.authService.canEditBip();
        if (!this.canEditBip) {
            this.isCurrentView = true;
        }

        const useOffline = this.offlineDataService.isInOfflineMode();
        if (useOffline) {
            this.loadDataOffline();
        } else {
            this.loadData();
        }
    }

    loadData() {
        const patientIdNum = Number(this.patientID());
        this.loading.set(true);

        if (!this.canEditBip) {
            forkJoin({
                patient: this.staffApiService.GetPatientInfo({ id: patientIdNum }),
                behaviors: this.staffApiService.GetPatientBehaviors({ patientID: patientIdNum, onlyActive: true }),
                functions: this.staffApiService.GetBehaviorFunctions(),
                current: this.staffApiService.GetCurrentBehaviorBIPInfos({ patientID: patientIdNum })
            }).subscribe({
                next: ({ patient, behaviors, functions, current }) => {
                    this.patient.set(patient ?? null);
                    this.functions.set(functions ?? []);
                    const behaviorMap = new Map<number, string>();
                    (behaviors ?? []).forEach(b => {
                        if (b.ID != null) {
                            behaviorMap.set(b.ID, b.PatientBehaviorName ?? '');
                        }
                    });
                    this.behaviorsLookup.set(behaviorMap);
                    this.behaviorsList.set(behaviors ?? []);

                    this.currentInfos.set((current ?? []).filter(x => (x?.PatientBehaviorID ?? 0) > 0));
                    this.draftInfos.set([]);

                    this.behaviorsCurrent.set(this.mapBehaviorsToRows(this.currentInfos(), this.behaviorsList()));
                    this.behaviorsDraft.set([]);

                    this.updateDisplayedList();
                    this.loading.set(false);
                },
                error: () => {
                    this.loading.set(false);
                    this.behaviors.set([]);
                }
            });
            return;
        }

        forkJoin({
            patient: this.staffApiService.GetPatientInfo({ id: patientIdNum }),
            behaviors: this.staffApiService.GetPatientBehaviors({ patientID: patientIdNum, onlyActive: true }),
            functions: this.staffApiService.GetBehaviorFunctions(),
            current: this.staffApiService.GetCurrentBehaviorBIPInfos({ patientID: patientIdNum }),
            draft: this.staffApiService.GetNewBehaviorBIPInfos({ patientID: patientIdNum })
        }).subscribe({
            next: ({ patient, behaviors, functions, current, draft }) => {
                this.patient.set(patient ?? null);
                this.functions.set(functions ?? []); // only used to map FunctionIDs from BIPInfos to names
                const behaviorMap = new Map<number, string>();
                (behaviors ?? []).forEach(b => {
                    if (b.ID != null) {
                        behaviorMap.set(b.ID, b.PatientBehaviorName ?? '');
                    }
                });
                this.behaviorsLookup.set(behaviorMap);
                this.behaviorsList.set(behaviors ?? []);

                this.currentInfos.set((current ?? []).filter(x => (x?.PatientBehaviorID ?? 0) > 0));
                this.draftInfos.set((draft ?? []).filter(x => (x?.PatientBehaviorID ?? 0) > 0));

                // Compute display rows
                this.behaviorsCurrent.set(this.mapBehaviorsToRows(this.currentInfos(), this.behaviorsList()));
                this.behaviorsDraft.set(this.mapBehaviorsToRows(this.draftInfos(), this.behaviorsList()));

                // Initialize displayed list based on toggle
                this.updateDisplayedList();
                this.loading.set(false);
            },
            error: () => {
                this.loading.set(false);
                this.behaviors.set([]);
            }
        });
    }

    loadDataOffline() {
        const patientIdNum = Number(this.patientID());
        this.loading.set(true);
        const data = this.offlineDataService.getOfflinePatientData(patientIdNum);
        if (data) {
            this.patient.set(data.patient ?? null);
            const behaviorMap = new Map<number, string>();
            (data.behaviors ?? []).forEach(b => {
                if (b.ID != null) behaviorMap.set(b.ID, b.PatientBehaviorName ?? '');
            });
            this.behaviorsLookup.set(behaviorMap);
            this.behaviorsList.set(data.behaviors ?? []);
            this.functions.set(data.behaviorFunctions ?? []);
            const current = data.behaviorBipInfos ?? [];
            this.currentInfos.set((current ?? []).filter(x => (x?.PatientBehaviorID ?? 0) > 0));
            this.draftInfos.set([]);
            this.behaviorsCurrent.set(this.mapBehaviorsToRows(this.currentInfos(), this.behaviorsList()));
            this.behaviorsDraft.set([]);
            this.updateDisplayedList();
        } else {
            this.behaviors.set([]);
        }
        this.loading.set(false);
    }

    onViewChange(e: any) {
        if (!this.canEditBip) {
            this.isCurrentView = true;
            this.updateDisplayedList();
            return;
        }
        this.isCurrentView = !!e?.value;
        this.updateDisplayedList();
    }

    private updateDisplayedList() {
        this.behaviors.set(this.isCurrentView ? this.behaviorsCurrent() : this.behaviorsDraft());
    }

    private mapBehaviorsToRows(infos: BehaviorBipInfoModel[], behaviors: PatientBehaviorModel[]): QuickRefBehaviorRow[] {
        const rows: QuickRefBehaviorRow[] = [];
        const map = this.behaviorsLookup();
        const infoByBehaviorId = new Map<number, BehaviorBipInfoModel>();
        for (const info of infos) {
            const bid = info.PatientBehaviorID ?? 0;
            if (bid > 0) infoByBehaviorId.set(bid, info);
        }
        for (const b of behaviors) {
            const id = b.ID ?? 0;
            const name = map.get(id) ?? (b.PatientBehaviorName ?? '');
            const info = infoByBehaviorId.get(id);
            const functionItems = this.mapFunctionIdsToItems(info);
            const antecedents = (info?.Antecedents ?? []).map(a => ({ id: a.ID ?? undefined, name: a.Name ?? '' }));
            const replacements = (info?.Replacements ?? []).map(r => ({ id: r.ID ?? undefined, name: r.Name ?? '' }));
            const consequences = (info?.Consequences ?? []).map(c => ({ id: c.ID ?? undefined, name: c.Name ?? '' }));
            rows.push({ id, name, functions: functionItems, antecedents, replacements, consequences });
        }
        return rows;
    }

    private mapFunctionIdsToItems(info?: BehaviorBipInfoModel | null): BipFunctionDisplayItem[] {
        return mapBehaviorBipFunctionsToDisplay(
            this.functions(),
            info?.Functions,
            info?.FunctionIDs
        );
    }

    // Header helpers
    clientName(): string {
        return this.patient()?.PatientName ?? '';
    }

    ageAndDOB(): string {
        if (!this.patient()?.PatientBirthdate) return '';
        return formatDate(this.patient()?.PatientBirthdate ?? '', 'MMM d, yyyy', 'en-US');
    }

    // Navigation to Behaviors page with fragment
    goToStrategy(kind: 'A' | 'R' | 'C', behaviorId: number, item: { id?: number | null; name?: string | null | undefined }) {
        const fragment = this.buildFragment(kind, behaviorId, item);
        if (this.isInStaffContext()) {
            this.strategySelected.emit({ fragment });
            return;
        }
        const view = !this.canEditBip || this.isCurrentView ? 'current' : 'draft';
        this.router.navigate([ '/supervisor', this.patientID(), 'BIP', 'Behaviors' ], { fragment, queryParams: { view } });
    }

    private buildFragment(kind: 'A' | 'R' | 'C', behaviorId: number, item: { id?: number | null; name?: string | null | undefined }): string {
        const key = item.id != null ? String(item.id) : this.slug(item.name ?? '');
        return `${kind}-${behaviorId}-${key}`;
    }

    private slug(value: string): string {
        return value
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-');
    }
}



interface QuickRefBehaviorRow {
    id: number;
    name: string;
    functions: BipFunctionDisplayItem[];
    antecedents: { id?: number | null; name: string | null | undefined }[];
    replacements: { id?: number | null; name: string | null | undefined }[];
    consequences: { id?: number | null; name: string | null | undefined }[];
}
